import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Mandala from "@/components/Mandala";
import SEO from "@/components/SEO";

const AVIANA_PORTRAIT = "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/cjp20hb7_Designer%20%285%29.png";
const HERO_BG = "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/zbhwvo23_SEDONARETREAT-131.png";

const PILLARS = [
  { name: "Sacred Embodiment", body: "Movement, breath, and awareness as portals to inner liberation." },
  { name: "Energetic Intelligence", body: "Cultivating emotional fluidity, heart-centered intention, and empowered presence." },
  { name: "Playful Discovery", body: "Curiosity, imagination, and childlike wonder as guides to freedom." },
  { name: "Community & Support", body: "Shared spaces that foster trust, belonging, and collective uplift." },
];

const PRINCIPLES = [
  { num: "01", planet: "Jupiter", title: "Cultivation of an Environment for Growth",
    body: "A welcoming, safe container in which students can soften into the practice. Instructors anchor with rooted, gentle presence — meeting each student where she stands.",
    motto: "Root to rise — welcome the discovery of just how far and high you can fly." },
  { num: "02", planet: "Neptune", title: "Embodiment of Inspiration",
    body: "Aerial yoga as a practice of imagination and possibility. Instructors guide as sources of inspiration, dissolving boundaries so fresh awareness can land.",
    motto: "Become Inspired: Be In Spirit." },
  { num: "03", planet: "Mars", title: "Integrity",
    body: "A daily check-in: Am I living my truth and practicing what I preach? Integrity is the soil from which all truths blossom.",
    motto: "Align with what speaks to your heart and live its Truth." },
  { num: "04", planet: "Venus", title: "Purposeful Intentions",
    body: "Each class begins with an intention — self-love, gratitude, compassion, grace, playfulness — so attention has a sacred place to rest. Love is the ultimate intention.",
    motto: "When it all boils down, there is only Fear and Love — Be, Live, and Do Love." },
  { num: "05", planet: "Moon", title: "Lunar Fluidity of Emotions",
    body: "Emotions are the feminine within every body. Aerial yoga touches trust, security, and self-esteem; we move at the student's pace, riding the wave of the breath.",
    motto: "Ride the wave of the breath and invite release as you find fluidity, ease, and grace." },
  { num: "06", planet: "Mercury", title: "Poetry in Motion",
    body: "The verbiage an instructor uses shapes the experience. Cues are crafted to melt the body into place — never to strain or force.",
    motto: "Let grace be your guide and surrender your chariot." },
  { num: "07", planet: "Sun", title: "Presence and Purpose",
    body: "Find enough stillness to hear the whispers of your heart. Recognize your own iron presence. You are whole, complete, and infinite.",
    motto: "Live Boldly and Brightly." },
  { num: "08", planet: "Saturn", title: "Cultivation of Energy through the Breath",
    body: "Everything comes back to and starts with the breath. Saturn's discipline meets Pranayama — the gateway between body and subtle mind.",
    motto: "Take responsibility for flowing with the breath." },
  { num: "09", planet: "Earth", title: "Earth's Energy",
    body: "Plant essences integrate into the energetic, physical, emotional, and mental bodies. At least one essence in every class supports peace, fulfillment, and enlightenment.",
    motto: "The therapeutic aromas of the plants amplify each intention." },
  { num: "10", planet: "Uranus", title: "Playful Adventure of Asanas",
    body: "Silks invite exhilaration. Play with the asanas. Discover trust, confidence, grace, and joy at any age, shape, or size.",
    motto: "Laughter in class is encouraged. Connect with your inner child and let yourself play." },
  { num: "11", planet: "Pluto", title: "Oneness",
    body: "Aviana sees the light within you and honors the same light reflected back. The ego's illusion of separation dissolves in the calm inner light.",
    motto: "Invite in Pluto to shed the layers between our hearts and our infinite connection." },
];

export default function Aviana() {
  return (
    <div data-testid="aviana-page">
      <SEO
        title="About Aviana — The Galactic Goddess Inspiring Our Practice"
        description="Aviana — galactic goddess of the Pleiades — is the divine inspiration behind our aerial yoga school. Discover the four energetic roots, eleven planetary principles, and the sacred-feminine philosophy guiding every retreat, teacher training, and Sister Circle."
        path="/aviana"
        image="/social/og-restorative-ytt.jpg"
        keywords="Aviana goddess, divine feminine yoga philosophy, sacred feminine practice, galactic goddess Pleiades, aerial yoga principles, embodiment yoga, women's spiritual practice"
      />
      {/* HERO — plum invocation */}
      <section className="relative min-h-[80svh] flex items-center overflow-hidden bg-aviana-plumDeep text-aviana-cream">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-aviana-plumDeep/85 via-aviana-plumDeep/70 to-aviana-plumDeep" />
        <div className="absolute inset-0 grain pointer-events-none opacity-50" aria-hidden />
        <Mandala size={900} variant="full" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-aviana-gold opacity-[0.10] animate-spin-slow" />
        <div className="container-aviana relative z-10 py-32 text-center">
          <div className="flex items-center justify-center gap-4 mb-8">
            <Mandala size={36} variant="lotus" className="text-aviana-goldSoft" />
            <p className="overline text-aviana-goldSoft">A Sanctuary for transformation</p>
            <Mandala size={36} variant="lotus" className="text-aviana-goldSoft" />
          </div>
          <h1 className="font-display text-[64px] sm:text-[88px] lg:text-[120px] leading-[0.92] tracking-[-0.01em]" data-testid="aviana-headline">
            About <em className="italic font-serif gold-text">Aviana.</em>
          </h1>
          <p className="mt-10 max-w-2xl mx-auto font-serif italic text-[22px] md:text-[26px] leading-[1.45] text-aviana-cream/90">
            Transformation Through <strong className="not-italic font-medium text-aviana-cream">Embodied Presence.</strong>
          </p>
        </div>
      </section>

      {/* INVITATION */}
      <section className="py-28 md:py-36">
        <div className="container-aviana max-w-4xl">
          <p className="overline-plum mb-5">A living invitation</p>
          <h2 className="font-display text-5xl md:text-7xl text-aviana-ink leading-[1.02]">
            Life is meant to be <em className="italic font-serif gold-text">felt fully.</em>
          </h2>
          <p className="mt-10 text-[19px] text-aviana-muted leading-[1.85]">
            At Aviana, we believe life is meant to be <em>felt fully</em> and <em>risen into with grace, courage, and radiant presence</em>. Aviana isn't just a name — it's a living invitation to awaken your <strong className="text-aviana-ink font-medium">inherent divine spark</strong>, embody your soul's wisdom, and deepen your connection to body, breath, and inner truth.
          </p>
          <p className="mt-6 text-[17px] text-aviana-muted leading-[1.85]">
            Our mission is to <strong className="text-aviana-ink font-medium">cultivate environments where transformation naturally unfolds</strong> — spaces that are safe, nurturing, spiritually expansive, and deeply human. Through aerial yoga, energetic healing, sisterhood gatherings, retreats, teacher trainings, and immersive experiences, Aviana supports each woman in stepping into a more embodied, expressive, and authentic life.
          </p>
        </div>
      </section>

      {/* FOUR ROOTS */}
      <section className="py-24 md:py-32 bg-aviana-surface" data-testid="aviana-roots">
        <div className="container-aviana">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="overline mb-5">The four energetic roots</p>
            <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.02]">
              Not abstract ideas — <em className="italic font-serif gold-text">living frequencies.</em>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((p, i) => (
              <div key={p.name} className="card-aviana p-8 text-center" data-testid={`aviana-root-${i}`}>
                <Mandala size={56} variant={["sun", "star", "rose", "geo"][i]} className="text-aviana-gold mx-auto mb-6" />
                <h3 className="font-display text-2xl text-aviana-ink mb-3">{p.name}</h3>
                <p className="text-sm text-aviana-muted leading-[1.7]">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE GODDESS */}
      <section className="py-28 md:py-36 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden" data-testid="aviana-goddess">
        <div className="absolute inset-0 grain pointer-events-none opacity-60" aria-hidden />
        <div className="container-aviana relative grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <div className="relative max-w-md mx-auto">
              <div className="absolute -inset-4 rounded-[3rem] bg-gradient-to-br from-aviana-goldSoft via-aviana-gold to-aviana-plum opacity-20 blur-2xl" />
              <div className="relative aspect-[2/3] rounded-[2rem] overflow-hidden ring-2 ring-aviana-gold/40">
                <img src={AVIANA_PORTRAIT} alt="Aviana — soul portrait" className="w-full h-full object-cover" />
              </div>
              <Mandala size={72} variant="star" className="absolute -top-5 -right-5 text-aviana-goldSoft opacity-90" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="overline mb-5 text-aviana-goldSoft">Who is the goddess Aviana</p>
            <h2 className="font-display text-5xl md:text-6xl leading-[1.05]">
              Honoring the <em className="italic font-serif gold-text">grace of the goddess</em> within us.
            </h2>
            <p className="mt-9 font-serif italic text-[20px] md:text-[22px] leading-[1.55] text-aviana-cream/90">
              Aviana is a <strong className="not-italic font-medium text-aviana-cream">galactic goddess</strong> born into the seven sisters of Pleiades.
            </p>
            <p className="mt-6 text-aviana-cream/80 leading-[1.85] text-[16px]">
              She embodies a strong feminine presence — the kind that is respected and honored by all who catch a glimpse of her. By night, she dances in moonlight with her sisters in honor of Mother Earth. By day she is sophisticated, gentle, and kind: <em>elegant grace with poise, posture, purpose, and patience</em>. She is a princess in the Divine Kingdom and a steward of Earth — loving every tree, stone, insect, animal, plant, and person.
            </p>
            <p className="mt-5 text-aviana-cream/80 leading-[1.85] text-[16px]">
              Her heart is liberated. She is <strong className="text-aviana-cream font-medium">healthy freedom at its finest.</strong>
            </p>
            <div className="mt-9 pt-7 border-t border-aviana-gold/20">
              <p className="overline text-aviana-goldSoft mb-4">She offers you</p>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[14px] text-aviana-cream/85">
                <li className="flex gap-3"><span className="text-aviana-goldSoft mt-[7px] text-[8px]">◆</span>An open &amp; liberated heart</li>
                <li className="flex gap-3"><span className="text-aviana-goldSoft mt-[7px] text-[8px]">◆</span>Aligned chakras of spinning illumination</li>
                <li className="flex gap-3"><span className="text-aviana-goldSoft mt-[7px] text-[8px]">◆</span>Grace through inspiration &amp; passion</li>
                <li className="flex gap-3"><span className="text-aviana-goldSoft mt-[7px] text-[8px]">◆</span>Dignified, lengthened, toned elegance</li>
                <li className="flex gap-3 sm:col-span-2"><span className="text-aviana-goldSoft mt-[7px] text-[8px]">◆</span>Embodiment of divine femininity</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* THE 11 PRINCIPLES — galactic roots */}
      <section className="py-28 md:py-36" data-testid="aviana-principles">
        <div className="container-aviana max-w-5xl">
          <div className="text-center mb-20">
            <p className="overline mb-5">The galactic roots of the goddess</p>
            <h2 className="font-display text-5xl md:text-7xl text-aviana-ink leading-[1.02]">
              Eleven <em className="italic font-serif gold-text">principles.</em>
            </h2>
            <p className="mt-7 max-w-2xl mx-auto text-aviana-muted leading-[1.85] font-serif italic text-[18px]">
              Each planet offers a frequency. Each frequency shapes how we hold space, breathe, teach, and grow.
            </p>
          </div>
          <div className="space-y-3">
            {PRINCIPLES.map((p) => (
              <details key={p.num} className="group bg-aviana-surface rounded-[1.5rem] border border-aviana-border overflow-hidden transition-all" data-testid={`principle-${p.num}`}>
                <summary className="cursor-pointer p-7 flex items-center gap-6 list-none hover:bg-aviana-cream transition-colors">
                  <span className="font-display text-3xl text-aviana-gold">{p.num}</span>
                  <div className="flex-1">
                    <p className="overline-plum">{p.planet}</p>
                    <h3 className="font-display text-2xl md:text-3xl text-aviana-ink mt-1">{p.title}</h3>
                  </div>
                  <span className="text-aviana-gold text-2xl transition-transform group-open:rotate-45">+</span>
                </summary>
                <div className="px-7 pb-7 pl-[88px]">
                  <p className="text-aviana-muted leading-[1.85] text-[16px]">{p.body}</p>
                  <p className="mt-5 font-serif italic text-aviana-ink text-[17px] border-l-2 border-aviana-gold pl-5">
                    {p.motto}
                  </p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="py-28 md:py-40 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none opacity-60" aria-hidden />
        <Mandala size={780} variant="full" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-aviana-gold opacity-[0.10] animate-spin-slow" />
        <div className="container-aviana relative text-center max-w-3xl mx-auto">
          <p className="overline mb-6 text-aviana-goldSoft">A living invitation</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[1.02]">
            Beyond <em className="italic font-serif gold-text">perfection.</em>
            <br />
            Into <em className="italic font-serif gold-text">presence.</em>
          </h2>
          <p className="mt-10 text-aviana-cream/85 leading-[1.85] font-serif italic text-[20px]">
            We're here to walk alongside your evolution — through every breath, every release, and every moment of <em className="not-italic font-medium text-aviana-goldSoft">becoming.</em>
          </p>
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <Link to="/retreats" className="btn-gold" data-testid="aviana-retreats-cta">
              Explore Retreats <ArrowRight size={16} />
            </Link>
            <Link to="/about" className="text-[12px] tracking-[0.22em] uppercase text-aviana-goldSoft hover:text-aviana-cream self-center">
              Meet Holly →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
